import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, profileAPI } from '../api/auth';
import OfficialBadge from '../components/OfficialBadge';

const EditProfile = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('');
  const [nickname, setNickname] = useState('');
  const [bio, setBio] = useState('');
  const [avatar, setAvatar] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const loadUser = async () => {
      try {
        const userData = await authAPI.getMe(token);
        setUser(userData);
        setUsername(userData.username || '');
        setNickname(userData.nickname || '');
        setBio(userData.bio || '');
        setAvatar(userData.avatar || '');
        setAvatarPreview(userData.avatar || '');
      } catch (err) {
        navigate('/login');
      }
    };

    loadUser();
  }, [navigate]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Пожалуйста, выберите изображение');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Изображение слишком большое. Максимум 5MB');
      return;
    }

    setUploadingImage(true);
    setError('');

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target.result;
      setAvatar(base64String);
      setAvatarPreview(base64String);
      setUploadingImage(false);
    };
    reader.onerror = () => {
      setError('Ошибка загрузки изображения');
      setUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const updated = await profileAPI.updateProfile(token, username || null, nickname || null, avatar || null, bio || null);
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
      setSuccess(true);
      setTimeout(() => {
        navigate('/messenger');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Ошибка обновления профиля');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatar('');
    setAvatarPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/messenger')} className="text-gray-600 hover:text-gray-800">← Назад</button>
          <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Редактировать профиль</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">✓ Профиль обновлен!</div>}

          <div className="text-center mb-8">
            <div className="relative inline-block">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-purple-200" />
              ) : (
                <div className="w-32 h-32 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mx-auto flex items-center justify-center text-white text-5xl font-bold mb-4">
                  {username?.charAt(0).toUpperCase()}
                </div>
              )}

              {avatarPreview && (
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 transition"
                  title="Удалить фото"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="flex gap-3 justify-center mt-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 transition disabled:opacity-50 font-semibold"
              >
                {uploadingImage ? 'Загрузка...' : '📷 Загрузить фото'}
              </button>
            </div>

            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

            <div className="mt-2 flex items-center justify-center gap-2 text-sm text-gray-600">
              <span>@{username}</span>
              <OfficialBadge username={username} />
            </div>
            <p className="text-sm text-gray-500 mt-2">Выберите фото из галереи (макс. 5MB)</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-gray-800 font-bold text-sm">Username (@username) *</label>
                <OfficialBadge username={username} />
              </div>
              <div className="relative">
                <span className="absolute left-4 top-3 text-gray-400">@</span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username"
                  className="w-full pl-9 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 transition-colors"
                  required
                  pattern="[a-zA-Z0-9_]{3,30}"
                  data-testid="username-input"
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">⚠️ Изменение username обновит вашу ссылку @username везде</p>
            </div>

            <div className="mb-5">
              <label className="block text-gray-800 font-bold mb-2 text-sm">Nickname (отображаемое имя)</label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Иван Петров или Ivan Pro"
                maxLength="50"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 transition-colors"
                data-testid="nickname-input"
              />
              <p className="text-sm text-gray-500 mt-2">Это НЕ username. Ник отображается в профиле</p>
            </div>

            <div className="mb-6">
              <label className="block text-gray-800 font-bold mb-2 text-sm">Описание профиля</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Расскажите о себе..."
                maxLength="500"
                rows="4"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 transition-colors"
                data-testid="bio-input"
              />
              <p className="text-sm text-gray-500 mt-1">{bio.length}/500 символов</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-lg py-4 rounded-xl hover:opacity-90 transition disabled:opacity-50 shadow-lg"
              data-testid="save-profile-button"
            >
              {loading ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
